import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { login } from '../../services/authService';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Lock, Scale } from 'lucide-react';
import './LoginPage.css';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Hooks reales de la aplicación
    const navigate = useNavigate();
    const { login: contextLogin } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username || !password) {
            setError('Por favor, complete ambos campos.');
            return;
        }
        setError(null);
        setIsLoading(true);
        try {
            // Se llama al servicio real de login
            const loginData = await login({ username, password });
            // Se actualiza el estado global de autenticación
            contextLogin(loginData);
            // Se redirige al usuario al dashboard
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.message || 'Ocurrió un error inesperado.');
        } finally {
            setIsLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { staggerChildren: 0.1, delayChildren: 0.4 }
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { 
            y: 0, 
            opacity: 1,
            transition: { type: 'spring', stiffness: 120, damping: 15 }
        },
    };

    return (
        <div className="c-login-page">
            <motion.div 
                className="c-login-page__panel"
                initial={{ opacity: 0, y: 30, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
                {/* Columna Izquierda (Branding) */}
                <div className="c-login-page__panel-left">
                    <motion.div
                        className="c-login-page__logo"
                        initial={{ scale: 0, rotate: -30 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.4, duration: 0.7, type: 'spring' }}
                    >
                        <Scale size={50} strokeWidth={1.5} />
                    </motion.div>
                    <motion.h1 
                        className="c-login-page__welcome-title"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.5 }}
                    >
                        AncashAbogados
                    </motion.h1>
                    <motion.p 
                        className="c-login-page__welcome-text"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.5 }}
                    >
                        La plataforma definitiva para la gestión estratégica de casos.
                    </motion.p>
                </div>

                {/* Columna Derecha (Formulario) */}
                <div className="c-login-page__panel-right">
                    <motion.form 
                        onSubmit={handleSubmit} 
                        className="c-login-page__form"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        noValidate
                    >
                        <motion.h2 variants={itemVariants}>Bienvenido de Vuelta</motion.h2>
                        
                        <motion.div className="c-login-page__form-group" variants={itemVariants}>
                            <div className="c-login-page__input-wrapper">
                                <UserIcon size={18} className="c-login-page__input-icon" />
                                <input
                                    type="text"
                                    id="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                    placeholder=" " // Requerido para la animación de floating label
                                />
                                <label htmlFor="username">Nombre de Usuario</label>
                            </div>
                        </motion.div>

                        <motion.div className="c-login-page__form-group" variants={itemVariants}>
                            <div className="c-login-page__input-wrapper">
                                <Lock size={18} className="c-login-page__input-icon" />
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder=" "
                                />
                                <label htmlFor="password">Contraseña</label>
                            </div>
                        </motion.div>

                        <AnimatePresence>
                            {error && (
                                <motion.p 
                                    className="c-login-page__error-msg"
                                    initial={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
                                    animate={{ opacity: 1, height: 'auto', marginTop: '0.5rem', marginBottom: '1rem' }}
                                    exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {error}
                                </motion.p>
                            )}
                        </AnimatePresence>
                        
                        <motion.div className="c-login-page__actions" variants={itemVariants}>
                            <a href="#" className="c-login-page__forgot-link">¿Olvidó su contraseña?</a>
                            <motion.button type="submit" className="c-login-page__submit-btn" disabled={isLoading} whileHover={{ y: -3 }} whileTap={{ scale: 0.98 }}>
                                {isLoading ? 'Ingresando...' : 'Ingresar'}
                            </motion.button>
                        </motion.div>
                    </motion.form>
                </div>
            </motion.div>
        </div>
    );
};

export default LoginPage;